import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScoreBadge } from '@/components/score-badge';
import { NngRadar } from '@/components/nng-radar';
import { A11yBar } from '@/components/a11y-bar';
import { HistorySpark } from '@/components/history-spark';
import { Download, ExternalLink } from 'lucide-react';

interface LintFinding {
  severity: string;
  message: string;
}

interface SubScore {
  score: number;
  reasoning: string;
  pros: string[];
  cons: string[];
  lintSummary?: string | null;
  lintErrors?: number;
  lintWarnings?: number;
  lintFindings?: LintFinding[] | null;
}

interface NngHeuristic {
  id: string;
  name: string;
  score: number;
  evidence: string[];
}

interface NngSubScore {
  score: number;
  heuristics: NngHeuristic[];
  pros: string[];
  cons: string[];
}

interface AxeViolation {
  id: string;
  impact: string;
  description: string;
  nodes: number;
  helpUrl?: string;
}

interface AccessibilitySubScore {
  score: number;
  reasoning: string;
  violations: AxeViolation[];
  pros: string[];
  cons: string[];
}

interface CostBreakdown {
  inputTokens: number;
  outputTokens: number;
  costTotalUsd: number;
}

interface PercentileInfo {
  totalEvaluations: number;
  compositePercentile: number | null;
  nngPercentile: number | null;
  a11yPercentile: number | null;
  designMdPercentile: number | null;
  priorRunsForDomain: number;
  domainHistory: { at: string; composite: number }[];
}

export interface ReportData {
  jobId: string;
  url: string;
  domain: string;
  compositeScore: number;
  designMd: SubScore | null;
  nng: NngSubScore;
  accessibility: AccessibilitySubScore;
  cost: CostBreakdown;
  percentile: PercentileInfo | null;
  model: string;
  agentVersion: string;
  commit: string;
  durationMs: number;
  submittedAt: string;
  completedAt: string;
}

function ProsCons({ pros, cons }: { pros: string[]; cons: string[] }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-400">Pros</div>
        <ul className="space-y-1 text-sm">
          {pros.map((p, i) => (
            <li key={i} className="flex gap-2"><span className="text-emerald-400">+</span>{p}</li>
          ))}
        </ul>
      </div>
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-400">Cons</div>
        <ul className="space-y-1 text-sm">
          {cons.map((c, i) => (
            <li key={i} className="flex gap-2"><span className="text-amber-400">−</span>{c}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Percentile({ value, label }: { value: number | null; label: string }) {
  if (value == null) return null;
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/30 px-3 py-2 text-sm">
      <span className="font-mono font-semibold text-[var(--color-primary)]">{value}th</span>{' '}
      <span className="text-[var(--color-muted-foreground)]">percentile</span>{' '}
      <span>· {label}</span>
    </div>
  );
}

export function Report({ data }: { data: ReportData }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col items-center gap-6 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="mb-1 text-sm uppercase tracking-wider text-[var(--color-muted-foreground)]">
                Evaluating
              </div>
              <a
                href={data.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-2xl font-semibold hover:text-[var(--color-primary)]"
              >
                {data.domain}
                <ExternalLink className="h-4 w-4" />
              </a>
              <div className="mt-3 text-xs text-[var(--color-muted-foreground)]">
                Model <span className="font-mono">{data.model}</span> · v{data.agentVersion} ({data.commit.slice(0, 7)})
                · {(data.durationMs / 1000).toFixed(1)}s · cost{' '}
                <span className="font-mono">${data.cost.costTotalUsd.toFixed(4)}</span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <a
                  href={`/api/jobs/${data.jobId}/summary.md`}
                  className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
                  title="Action-oriented findings — paste into Slack / a PR / an issue"
                >
                  <Download className="h-3.5 w-3.5" /> Download summary.md
                </a>
                <a
                  href={`/api/jobs/${data.jobId}/report.md`}
                  className="inline-flex items-center gap-1 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                  title="Full descriptive report"
                >
                  <Download className="h-3.5 w-3.5" /> Download report.md
                </a>
              </div>
            </div>
            <ScoreBadge score={data.compositeScore} label="composite" size="lg" />
          </div>

          <div className={`mt-6 grid gap-3 ${data.designMd ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            {data.designMd && (
              <div className="rounded-lg border border-[var(--color-border)] p-4 text-center">
                <ScoreBadge score={data.designMd.score} />
                <div className="mt-2 text-sm text-[var(--color-muted-foreground)]">DESIGN.md alignment</div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--color-muted-foreground)] opacity-60">
                  weight 50%
                </div>
              </div>
            )}
            <div className="rounded-lg border border-[var(--color-border)] p-4 text-center">
              <ScoreBadge score={data.nng.score} />
              <div className="mt-2 text-sm text-[var(--color-muted-foreground)]">NN/g heuristics</div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--color-muted-foreground)] opacity-60">
                weight {data.designMd ? '25%' : '60%'}
              </div>
            </div>
            <div className="rounded-lg border border-[var(--color-border)] p-4 text-center">
              <ScoreBadge score={data.accessibility.score} />
              <div className="mt-2 text-sm text-[var(--color-muted-foreground)]">Accessibility</div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--color-muted-foreground)] opacity-60">
                weight {data.designMd ? '25%' : '40%'}
              </div>
            </div>
          </div>
          {!data.designMd && (
            <p className="mt-4 text-center text-xs text-[var(--color-muted-foreground)]">
              No DESIGN.md was provided; scored on NN/g + accessibility only.
            </p>
          )}
        </CardContent>
      </Card>

      {data.percentile && (data.percentile.totalEvaluations >= 2 || data.percentile.priorRunsForDomain > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>How this compares</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-[var(--color-muted-foreground)]">
              Across {data.percentile.totalEvaluations} prior evaluations:
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Percentile value={data.percentile.compositePercentile} label="Composite" />
              <Percentile value={data.percentile.nngPercentile} label="NN/g" />
              <Percentile value={data.percentile.a11yPercentile} label="Accessibility" />
              <Percentile value={data.percentile.designMdPercentile} label="DESIGN.md" />
            </div>
            {data.percentile.priorRunsForDomain > 1 && (
              <div className="pt-2">
                <div className="mb-1 text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">
                  Prior runs on {data.domain}
                </div>
                <HistorySpark points={data.percentile.domainHistory} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {data.designMd && (
        <Card>
          <CardHeader>
            <CardTitle>DESIGN.md alignment</CardTitle>
            {data.designMd.lintSummary && (
              <div className="mt-1 text-xs">
                <span
                  className={
                    (data.designMd.lintErrors ?? 0) > 0
                      ? 'rounded-md bg-rose-500/15 px-2 py-0.5 text-rose-300'
                      : (data.designMd.lintWarnings ?? 0) > 0
                        ? 'rounded-md bg-amber-500/15 px-2 py-0.5 text-amber-300'
                        : 'rounded-md bg-emerald-500/15 px-2 py-0.5 text-emerald-300'
                  }
                >
                  {data.designMd.lintSummary}
                </span>
                <span className="ml-2 text-[var(--color-muted-foreground)]">
                  via <code className="font-mono">@google/design.md lint</code>
                </span>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm">{data.designMd.reasoning}</p>
            <ProsCons pros={data.designMd.pros} cons={data.designMd.cons} />
            {data.designMd.lintFindings && data.designMd.lintFindings.length > 0 && (
              <details className="rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/20 px-3 py-2 text-sm">
                <summary className="cursor-pointer text-xs uppercase tracking-wider text-[var(--color-muted-foreground)]">
                  DESIGN.md lint findings ({data.designMd.lintFindings.length})
                </summary>
                <ul className="mt-2 space-y-1.5 text-xs">
                  {data.designMd.lintFindings.map((f, i) => {
                    const sev = f.severity.toLowerCase();
                    const tone =
                      sev === 'error' ? 'text-rose-300'
                        : sev === 'warning' ? 'text-amber-300'
                          : 'text-[var(--color-muted-foreground)]';
                    return (
                      <li key={i} className="flex gap-2">
                        <span className={`flex-shrink-0 font-mono uppercase tracking-wide ${tone}`}>
                          [{f.severity}]
                        </span>
                        <span>{f.message}</span>
                      </li>
                    );
                  })}
                </ul>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>NN/g heuristics — 10 dimensions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <NngRadar heuristics={data.nng.heuristics} />
          <details className="text-sm">
            <summary className="cursor-pointer text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
              Per-heuristic scores + evidence
            </summary>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs text-[var(--color-muted-foreground)]">
                  <tr>
                    <th className="px-2 py-1">Heuristic</th>
                    <th className="px-2 py-1">Score</th>
                    <th className="px-2 py-1">Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {data.nng.heuristics.map((h) => (
                    <tr key={h.id} className="border-t border-[var(--color-border)]">
                      <td className="px-2 py-1 font-medium">{h.name}</td>
                      <td className="px-2 py-1 font-mono">{h.score}</td>
                      <td className="px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
                        {h.evidence.map((e, i) => <div key={i}>• {e}</div>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
          <ProsCons pros={data.nng.pros} cons={data.nng.cons} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Accessibility</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm">{data.accessibility.reasoning}</p>
          <A11yBar violations={data.accessibility.violations} />
          {data.accessibility.violations.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                Violation details
              </summary>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-xs text-[var(--color-muted-foreground)]">
                    <tr>
                      <th className="px-2 py-1">Rule</th>
                      <th className="px-2 py-1">Impact</th>
                      <th className="px-2 py-1">Description</th>
                      <th className="px-2 py-1">Nodes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.accessibility.violations.map((v, i) => (
                      <tr key={i} className="border-t border-[var(--color-border)]">
                        <td className="px-2 py-1 font-mono text-xs">
                          {v.helpUrl ? (
                            <a href={v.helpUrl} target="_blank" rel="noreferrer" className="text-[var(--color-primary)] hover:underline">{v.id}</a>
                          ) : v.id}
                        </td>
                        <td className="px-2 py-1">{v.impact}</td>
                        <td className="px-2 py-1 text-xs">{v.description}</td>
                        <td className="px-2 py-1 font-mono">{v.nodes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
          <ProsCons pros={data.accessibility.pros} cons={data.accessibility.cons} />
        </CardContent>
      </Card>
    </div>
  );
}
